/**
 * Service for resolving and registering type_ouvrage markers on the map.
 * Used when style has metadata.inventoryAnalysis.typeProperty === "type_ouvrage".
 * Fetches GET /api/admin/type_ouvrages, builds typeCode → markerConfig map,
 * resolves icon source (lucideIcon > iconUrl > sprite+iconName > default) and registers images on the map.
 */
import type maplibregl from 'maplibre-gl'
import { getTypeOuvrages } from '@/services/admin/AdminParametrageService'
import type { ClassificationValue, TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import type { MarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import { MarkerGeneratorService } from './MarkerGeneratorService'
import { resolveIconLoadUrl } from '@/services/map/mapIconCatalog'
import { apiClient } from '@/plugins/axios/api'

const DEFAULT_MARKER_COLOR = '#3aa2bc'
const DEFAULT_ICON_COLOR = '#ffffff'
const DEFAULT_ICON_SIZE = 0.6

export interface TypeOuvrageMarkerResult {
  typeToImageId: Record<string, string>
  defaultImageId: string
}

/**
 * True when a marker_config is missing or carries no usable value (no icon source, no color).
 * Types with an empty marker_config keep the current (style-derived) rendering everywhere.
 */
export function isMarkerConfigEmpty(config: TypeOuvrageMarkerConfig | null | undefined): boolean {
  if (!config) return true
  return !(
    config.lucideIcon?.trim() ||
    config.iconUrl?.trim() ||
    config.sprite?.trim() ||
    config.markerColor?.trim() ||
    config.iconColor?.trim()
  )
}

/** Normalize API type d'ouvrage to get markerConfig (supports markerConfig and marker_config). */
function getMarkerConfigFromType(item: {
  typeOuvrageCode?: string
  type_ouvrage_code?: string
  markerConfig?: TypeOuvrageMarkerConfig | null
  marker_config?: TypeOuvrageMarkerConfig | null
}): TypeOuvrageMarkerConfig | null {
  const config = item.markerConfig ?? item.marker_config
  if (config == null || typeof config !== 'object') return null
  return config
}

/**
 * Builds a map typeOuvrageCode → markerConfig from GET /api/admin/type_ouvrages.
 */
export async function getTypeToMarkerConfigMap(): Promise<
  Record<string, TypeOuvrageMarkerConfig | null>
> {
  const list = await getTypeOuvrages()
  const map: Record<string, TypeOuvrageMarkerConfig | null> = {}
  for (const item of list) {
    const code = item.typeOuvrageCode ?? item.type_ouvrage_code ?? ''
    if (code) {
      map[code] = getMarkerConfigFromType(item)
    }
  }
  return map
}

/**
 * Resolves effective marker config for a type: use type's config or default (metadata.marker).
 * Resolution order for icon source: lucideIcon > iconUrl > sprite+iconName; then fallback to default.
 */
export function resolveEffectiveConfig(
  _typeCode: string,
  typeConfig: TypeOuvrageMarkerConfig | null | undefined,
  defaultMarker: Partial<MarkerConfig> | null | undefined,
): {
  markerColor: string
  lucideIcon?: string
  iconColor: string
  iconSize: number
  iconUrl?: string
  sprite?: string
  iconName?: string
  source: 'lucide' | 'url' | 'sprite' | 'default'
} {
  const def = defaultMarker ?? {}
  const markerColor = typeConfig?.markerColor ?? def.markerColor ?? DEFAULT_MARKER_COLOR
  const iconColor = typeConfig?.iconColor ?? def.iconColor ?? DEFAULT_ICON_COLOR
  const iconSize = typeConfig?.iconSize ?? def.iconSize ?? DEFAULT_ICON_SIZE

  if (typeConfig?.lucideIcon) {
    return {
      markerColor,
      lucideIcon: typeConfig.lucideIcon,
      iconColor,
      iconSize,
      source: 'lucide',
    }
  }
  if (typeConfig?.iconUrl) {
    return {
      markerColor,
      iconColor,
      iconSize,
      iconUrl: typeConfig.iconUrl,
      source: 'url',
    }
  }
  if (typeConfig?.sprite && typeConfig?.iconName) {
    return {
      markerColor,
      iconColor,
      iconSize,
      sprite: typeConfig.sprite,
      iconName: typeConfig.iconName,
      source: 'sprite',
    }
  }
  return {
    markerColor,
    lucideIcon: def.lucideIcon,
    iconColor,
    iconSize,
    source: 'default',
  }
}

/**
 * Ensures all type_ouvrage marker images are registered on the map and returns typeCode → imageId.
 * Uses defaultMarker (e.g. from style.metadata.marker) for types without config or for sprite fallback.
 */
export async function ensureTypeOuvrageImagesOnMap(
  map: maplibregl.Map,
  indicatorKey: string | number,
  typeToConfig: Record<string, TypeOuvrageMarkerConfig | null>,
  defaultMarker: Partial<MarkerConfig> | null | undefined,
): Promise<TypeOuvrageMarkerResult> {
  // Clear SVG cache so color changes take effect on every call.
  MarkerGeneratorService.clearCache()

  const prefix = `type-ouvrage-${indicatorKey}`
  const defaultImageId = `${prefix}-default`
  const typeToImageId: Record<string, string> = {}

  // Remove previously registered images for this indicator so they are regenerated with the current config.
  if (map.hasImage(defaultImageId)) map.removeImage(defaultImageId)
  for (const typeCode of Object.keys(typeToConfig)) {
    const imageId = `${prefix}-${typeCode}`
    if (map.hasImage(imageId)) map.removeImage(imageId)
  }

  const defaultResolved = resolveEffectiveConfig('', null, defaultMarker)
  const defaultMarkerColor = defaultResolved.markerColor
  const defaultLucideIcon = defaultResolved.lucideIcon
  const defaultIconColor = defaultResolved.iconColor

  async function addDefaultImage(): Promise<void> {
    if (map.hasImage(defaultImageId)) return
    const markerSvg = await MarkerGeneratorService.generateMarker(
      defaultMarkerColor,
      defaultLucideIcon,
      defaultIconColor,
      false,
    )
    const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
    if (img) map.addImage(defaultImageId, img)
  }

  await addDefaultImage()

  const typeCodes = Object.keys(typeToConfig)
  for (const typeCode of typeCodes) {
    const config = typeToConfig[typeCode]
    const resolved = resolveEffectiveConfig(typeCode, config, defaultMarker)
    const imageId = `${prefix}-${typeCode}`

    if (map.hasImage(imageId)) {
      typeToImageId[typeCode] = imageId
      continue
    }

    if (resolved.source === 'lucide' && resolved.lucideIcon) {
      try {
        const markerSvg = await MarkerGeneratorService.generateMarker(
          resolved.markerColor,
          resolved.lucideIcon,
          resolved.iconColor,
          false,
        )
        const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
        map.addImage(imageId, img)
        typeToImageId[typeCode] = imageId
        console.debug(
          `[TypeOuvrageMarkerService] registered image "${imageId}" for icon "${resolved.lucideIcon}"`,
        )
      } catch (err) {
        console.error(
          `[TypeOuvrageMarkerService] failed to register image for "${typeCode}" (icon "${resolved.lucideIcon}"):`,
          err,
        )
        typeToImageId[typeCode] = defaultImageId
      }
      continue
    }

    if (resolved.source === 'url' && resolved.iconUrl) {
      try {
        // Resolve the icon URL: /media/... is used as-is; /api/media_objects/{id}
        // is first resolved via authenticated metadata fetch to obtain contentUrl.
        // The resulting /media/... URL is publicly accessible — no auth header needed.
        const absoluteUrl = await resolveIconBinaryUrl(resolved.iconUrl)
        const isSvg =
          absoluteUrl.toLowerCase().includes('.svg') ||
          resolved.iconUrl.toLowerCase().includes('.svg')

        if (isSvg) {
          // Fetch SVG text and embed it inside the pin shape so it renders like a Lucide icon.
          const svgText = await MarkerGeneratorService.fetchSvgFromUrl(absoluteUrl)
          const markerSvg = MarkerGeneratorService.createMarkerSvgWithIcon(
            svgText,
            resolved.markerColor,
          )
          const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
          map.addImage(imageId, img)
          typeToImageId[typeCode] = imageId
          console.debug(
            `[TypeOuvrageMarkerService] registered image "${imageId}" for SVG URL "${absoluteUrl}"`,
          )
        } else {
          const imageData = await loadImageAsImageData(absoluteUrl)
          if (imageData) {
            map.addImage(imageId, imageData)
            typeToImageId[typeCode] = imageId
            console.debug(
              `[TypeOuvrageMarkerService] registered image "${imageId}" for URL "${absoluteUrl}"`,
            )
          } else {
            typeToImageId[typeCode] = defaultImageId
          }
        }
      } catch (err) {
        console.error(
          `[TypeOuvrageMarkerService] failed to register image for "${typeCode}" (url "${resolved.iconUrl}"):`,
          err,
        )
        typeToImageId[typeCode] = defaultImageId
      }
      continue
    }

    if (resolved.source === 'sprite') {
      typeToImageId[typeCode] = defaultImageId
      continue
    }

    typeToImageId[typeCode] = defaultImageId
  }

  return { typeToImageId, defaultImageId }
}

/** Short deterministic hash fragment for a marker_config, so each distinct config gets its own image id. */
function hashMarkerConfig(config: TypeOuvrageMarkerConfig | null | undefined): string {
  const raw = JSON.stringify(config ?? {})
  let hash = 0
  for (let i = 0; i < raw.length; i++) {
    hash = (hash * 31 + raw.charCodeAt(i)) | 0
  }
  return Math.abs(hash).toString(36)
}

/** Rendered size (px) of a standalone marker_config icon badge on the map — larger than the old
 *  embedded-in-pin treatment so the picto reads clearly instead of shrinking inside a generic pin. */
const STANDALONE_ICON_MAP_SIZE = 56

/**
 * Resolves and registers a single marker_config-driven image on the map — used for OSM/infra
 * type-ouvrage point layers so the pins shown on the map match the icon configured for that type
 * d'ouvrage (and already shown in the right panel), instead of the flat colored pin used today.
 * The returned image id embeds the resolved color (`{idPrefix}-{colorHex}-{configHash}`) so callers
 * that need to read the color back (e.g. OsmClusterService, to match the cluster bubble color to
 * the icon) can parse it out of the id instead of re-resolving the config — see `color` in the
 * return value, which is the same value. `anchor` tells the caller how to anchor the symbol layer:
 * 'bottom' for our own teardrop pin (pointed tip on the ground point), 'center' for a standalone
 * icon badge (no pointed tip — the point is at the badge's center).
 * Returns null when the config is empty/unresolvable (caller should keep its current icon-less
 * rendering in that case — untouched per the "empty config" rule).
 * `forceColor`: for an uploaded SVG icon, ignore the badge's own embedded color and tint it with
 * `config.markerColor`/`fallbackColor` instead — used when recoloring for a selected classification
 * value (see registerClassificationValueImage), since the SVG's own baked-in color would otherwise
 * make every classification value render identically regardless of the requested color.
 */
export async function registerMarkerConfigImage(
  map: maplibregl.Map,
  imageIdPrefix: string,
  config: TypeOuvrageMarkerConfig | null | undefined,
  fallbackColor: string,
  forceColor = false,
): Promise<{ imageId: string; color: string; anchor: 'bottom' | 'center' } | null> {
  if (isMarkerConfigEmpty(config)) return null

  const resolved = resolveEffectiveConfig('', config, { markerColor: fallbackColor })
  const buildImageId = (color: string) => `${imageIdPrefix}-${color.replace('#', '')}-${hashMarkerConfig(config)}`

  try {
    if (resolved.source === 'url' && resolved.iconUrl) {
      const absoluteUrl = await resolveIconBinaryUrl(resolved.iconUrl)
      const isSvg =
        absoluteUrl.toLowerCase().includes('.svg') || resolved.iconUrl.toLowerCase().includes('.svg')

      if (isSvg) {
        const svgText = await MarkerGeneratorService.fetchSvgFromUrl(absoluteUrl)
        if (forceColor) {
          // Requested color takes over the badge entirely: render the icon inside our own
          // colored teardrop pin instead of the self-colored standalone badge below.
          const color = resolved.markerColor
          const imageId = buildImageId(color)
          if (map.hasImage(imageId)) return { imageId, color, anchor: 'bottom' }
          const markerSvg = MarkerGeneratorService.createMarkerSvgWithIcon(svgText, color)
          const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
          if (!map.hasImage(imageId)) map.addImage(imageId, img)
          return { imageId, color, anchor: 'bottom' }
        }
        // These backend-provided badges already carry their own color (background shape + white
        // picto) — use it instead of the shared default so each type d'ouvrage reads distinctly
        // instead of every icon defaulting to the same pin color.
        const color = MarkerGeneratorService.extractDominantFillColor(svgText) ?? resolved.markerColor
        const imageId = buildImageId(color)
        if (map.hasImage(imageId)) return { imageId, color, anchor: 'center' }
        const markerSvg = MarkerGeneratorService.createStandaloneIconMarker(svgText, STANDALONE_ICON_MAP_SIZE)
        const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
        if (!map.hasImage(imageId)) map.addImage(imageId, img)
        return { imageId, color, anchor: 'center' }
      }

      const imageId = buildImageId(resolved.markerColor)
      if (map.hasImage(imageId)) return { imageId, color: resolved.markerColor, anchor: 'center' }
      const imageData = await loadImageAsImageData(absoluteUrl)
      if (!imageData) return null
      if (!map.hasImage(imageId)) map.addImage(imageId, imageData)
      return { imageId, color: resolved.markerColor, anchor: 'center' }
    }

    // lucideIcon or color-only (source === 'default'/'sprite' with no dedicated art here): still
    // rendered inside our own colored teardrop pin.
    const imageId = buildImageId(resolved.markerColor)
    if (map.hasImage(imageId)) return { imageId, color: resolved.markerColor, anchor: 'bottom' }
    const markerSvg = await MarkerGeneratorService.generateMarker(
      resolved.markerColor,
      resolved.source === 'lucide' ? resolved.lucideIcon : undefined,
      resolved.iconColor,
      false,
    )
    const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
    if (!map.hasImage(imageId)) map.addImage(imageId, img)
    return { imageId, color: resolved.markerColor, anchor: 'bottom' }
  } catch (err) {
    console.error(`[TypeOuvrageMarkerService] failed to register marker_config image "${imageIdPrefix}":`, err)
    return null
  }
}

/**
 * Registers (or reuses) the marker image for a single classification value (e.g. "Publique" for
 * a school type d'ouvrage), tinted with that value's own color. Used to recolor a type d'ouvrage's
 * map pins to the one category currently selected in the right panel — see
 * OsmLayerHandler.applyClassificationColor, which falls back to the type d'ouvrage's own default
 * icon (captured on the layer's metadata) when no single value is selected.
 */
export async function registerClassificationValueImage(
  map: maplibregl.Map,
  imageIdPrefix: string,
  config: TypeOuvrageMarkerConfig | null | undefined,
  value: ClassificationValue,
): Promise<{ imageId: string; color: string; anchor: 'bottom' | 'center' } | null> {
  const perValueConfig: TypeOuvrageMarkerConfig = { ...(config ?? {}), markerColor: value.color }
  return registerMarkerConfigImage(map, imageIdPrefix, perValueConfig, value.color, true)
}

/** Admin-configured icon/color resolved for a type d'ouvrage, as a plain SVG string usable outside
 *  MapLibre (e.g. a Vue `<img>` data URL or an inline chart swatch) — see prefetchTypeOuvrageVisuals. */
export interface ResolvedTypeOuvrageVisual {
  /** Fully rendered pin marker SVG (color + icon baked in). */
  markerSvg: string
  /** The same marker color alone, for flat swatches (legend dots, donut/bar fills). */
  color: string
}

const resolvedTypeOuvrageVisuals = new Map<string, ResolvedTypeOuvrageVisual>()
let prefetchTypeOuvrageVisualsPromise: Promise<void> | null = null

/**
 * Prefetches, for every type d'ouvrage with a non-empty admin marker_config, a rendered marker SVG
 * + its resolved color — cached for synchronous reads via getResolvedTypeOuvrageVisual(). Lets
 * chart/legend UIs (outside MapLibre, so registerMarkerConfigImage's map-image registration doesn't
 * apply) match the same admin-configured appearance already used on the map and in the right panel,
 * instead of maintaining their own separate hardcoded icon/color catalog.
 * Safe to call repeatedly — the underlying fetch + resolution only runs once.
 */
export function prefetchTypeOuvrageVisuals(): Promise<void> {
  if (!prefetchTypeOuvrageVisualsPromise) {
    prefetchTypeOuvrageVisualsPromise = (async () => {
      const typeToConfig = await getTypeToMarkerConfigMap()
      await Promise.all(
        Object.entries(typeToConfig).map(async ([code, config]) => {
          if (isMarkerConfigEmpty(config)) return
          try {
            const resolved = resolveEffectiveConfig(code, config, null)
            if (resolved.source === 'url' && resolved.iconUrl) {
              const absoluteUrl = await resolveIconBinaryUrl(resolved.iconUrl)
              const isSvg =
                absoluteUrl.toLowerCase().includes('.svg') || resolved.iconUrl.toLowerCase().includes('.svg')
              if (!isSvg) return // raster icon: can't be recolored here, caller keeps its own fallback
              const svgText = await MarkerGeneratorService.fetchSvgFromUrl(absoluteUrl)
              const markerSvg = MarkerGeneratorService.createMarkerSvgWithIcon(svgText, resolved.markerColor)
              resolvedTypeOuvrageVisuals.set(code, { markerSvg, color: resolved.markerColor })
              return
            }
            const markerSvg = await MarkerGeneratorService.generateMarker(
              resolved.markerColor,
              resolved.source === 'lucide' ? resolved.lucideIcon : undefined,
              resolved.iconColor,
              false,
            )
            resolvedTypeOuvrageVisuals.set(code, { markerSvg, color: resolved.markerColor })
          } catch (err) {
            console.error(`[TypeOuvrageMarkerService] failed to resolve visual for type "${code}":`, err)
          }
        }),
      )
    })()
  }
  return prefetchTypeOuvrageVisualsPromise
}

/**
 * Synchronous read of a type d'ouvrage's prefetched marker visual. Returns undefined before
 * prefetchTypeOuvrageVisuals() resolves, or when that type has no admin marker_config (or only a
 * raster icon) — callers should fall back to their own default appearance in that case.
 */
export function getResolvedTypeOuvrageVisual(code: string): ResolvedTypeOuvrageVisual | undefined {
  return resolvedTypeOuvrageVisuals.get(code)
}

/**
 * Resolves an iconUrl to the binary image URL to actually load/fetch from.
 *
 * - /media/... paths → publicly accessible. Resolved via resolveIconLoadUrl, which in dev rewrites
 *   to a relative path so the Vite proxy forwards the request same-origin (avoids CORS errors when
 *   fetching the raw SVG text or reading canvas pixel data — the backend media host does not send
 *   Access-Control-Allow-Origin). In production this still returns the absolute cross-origin URL:
 *   the backend/media host needs to send CORS headers for this to work outside of dev.
 * - /api/media_objects/{id} → JSON metadata endpoint (requires auth).
 *   Uses apiClient (which injects the JWT automatically) to GET the metadata,
 *   extracts contentUrl, then resolves that the same way as a plain /media/... path.
 */
export async function resolveIconBinaryUrl(iconUrl: string): Promise<string> {
  if (!iconUrl.includes('/api/media_objects/')) {
    return resolveIconLoadUrl(iconUrl)
  }

  try {
    const response = await apiClient.get<{ contentUrl?: string }>(iconUrl)
    const contentUrl = (response.data.contentUrl ?? '').trim()
    if (!contentUrl) {
      console.warn('[TypeOuvrageMarkerService] empty contentUrl for media object', iconUrl)
      return resolveIconLoadUrl(iconUrl)
    }
    return resolveIconLoadUrl(contentUrl)
  } catch (err) {
    console.warn('[TypeOuvrageMarkerService] could not resolve media object metadata for', iconUrl, err)
    return resolveIconLoadUrl(iconUrl)
  }
}

/**
 * Loads an image from URL and returns ImageData for MapLibre addImage.
 */
function loadImageAsImageData(url: string): Promise<ImageData | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      resolve(imageData)
    }
    img.onerror = () => resolve(null)
    img.src = url
  })
}
